// src/components/divesitedashboard/DashboardTabs.jsx
import React from "react";
import { Paper, Tabs, Tab, useMediaQuery, alpha } from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";
import ScubaDivingIcon from "@mui/icons-material/ScubaDiving";
import CloudIcon from "@mui/icons-material/Cloud";
import WavesIcon from "@mui/icons-material/Waves";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GroupsIcon from "@mui/icons-material/Groups";
import BugReportIcon from "@mui/icons-material/BugReport";

// Glassmorphic container for tabs
const GlassTabsContainer = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.8)} 0%, 
    ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(20px) saturate(1.1)",
  WebkitBackdropFilter: "blur(20px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  borderRadius: 20,
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.06)}`,
  margin: theme.spacing(0, 2),

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.4)}, 
      ${alpha(theme.palette.secondary.main, 0.3)}, 
      transparent)`,
    borderRadius: "20px 20px 0 0",
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 20%, 
      ${alpha(theme.palette.primary.main, 0.03)} 0%, 
      transparent 50%), 
      radial-gradient(circle at 80% 80%, 
      ${alpha(theme.palette.secondary.main, 0.03)} 0%, 
      transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  [theme.breakpoints.down("md")]: {
    margin: theme.spacing(0, 1),
    borderRadius: 16,
  },

  [theme.breakpoints.down("sm")]: {
    margin: theme.spacing(0, 0.5),
    borderRadius: 12,
  },
}));

// Enhanced tabs with glassmorphic styling
const GlassTabs = styled(Tabs)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  "& .MuiTabs-indicator": {
    height: 3,
    borderRadius: "3px 3px 0 0",
    background: `linear-gradient(135deg, 
      ${theme.palette.primary.main} 0%, 
      ${theme.palette.secondary.main} 100%)`,
    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  "& .MuiTabs-scrollButtons": {
    color: theme.palette.primary.main,
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.primary.main, 0.1)} 0%, 
      ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
    backdropFilter: "blur(10px)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
    borderRadius: "50%",
    margin: theme.spacing(0, 0.5),
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.primary.main, 0.15)} 0%, 
        ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
      transform: "scale(1.05)",
    },
    "&.Mui-disabled": {
      opacity: 0.3,
    },
  },
  "& .MuiTab-root": {
    fontWeight: 500,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.primary.main, 0.08)} 0%, 
        ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
      opacity: 0,
      transition: "opacity 0.3s ease",
      borderRadius: 12,
      margin: theme.spacing(0.5),
    },
    "&:hover::before": {
      opacity: 1,
    },
    "&.Mui-selected": {
      color: theme.palette.primary.main,
      fontWeight: 600,
      "&::before": {
        opacity: 1,
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.main, 0.12)} 0%, 
          ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
      },
    },
    "& .MuiTab-iconWrapper": {
      transition: "transform 0.3s ease",
    },
    "&:hover .MuiTab-iconWrapper": {
      transform: "translateY(-1px) scale(1.05)",
    },
  },
}));

const DashboardTabs = ({ tabValue, handleTabChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <GlassTabsContainer elevation={0}>
      <GlassTabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="dashboard tabs"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          px: isMobile ? 1 : 2,
          minHeight: isMobile ? 48 : "auto",
          "& .MuiTabs-indicator": {
            height: isMobile ? 2 : 3,
          },
          "& .MuiTab-root": {
            minWidth: isMobile ? 80 : 120,
            padding: isMobile ? theme.spacing(1, 0.5) : theme.spacing(2, 2),
            fontSize: isMobile ? "0.75rem" : "0.875rem",
            ...(isMobile && {
              flexDirection: "column",
              "& .MuiTab-iconWrapper": {
                marginBottom: theme.spacing(0.5),
                marginRight: 0,
              },
            }),
            ...(isTablet &&
              !isMobile && {
                minWidth: 100,
                padding: theme.spacing(1.5, 1),
              }),
          },
        }}
      >
        <Tab
          icon={<ScubaDivingIcon sx={{ fontSize: isMobile ? 18 : 24 }} />}
          label="Divesites"
        />
        <Tab
          icon={<CloudIcon sx={{ fontSize: isMobile ? 18 : 24 }} />}
          label="Weather"
        />
        <Tab
          icon={<WavesIcon sx={{ fontSize: isMobile ? 18 : 24 }} />}
          label="Tides"
        />
        <Tab
          icon={<LocationOnIcon sx={{ fontSize: isMobile ? 18 : 24 }} />}
          label="Location"
        />
        <Tab
          icon={<GroupsIcon sx={{ fontSize: isMobile ? 18 : 24 }} />}
          label="Activity"
        />
        <Tab
          icon={<BugReportIcon sx={{ fontSize: isMobile ? 18 : 24 }} />}
          label="Creatures"
        />
      </GlassTabs>
    </GlassTabsContainer>
  );
};

export default DashboardTabs;
